"use client"

import { useState } from "react"
import Link from "next/link"
import { useAlertSubscribe } from "./use-alert-subscribe"

interface FilterReminderFormProps {
  /** Prefilled from finder results (result.filters[0].model); editable. */
  filterModel?: string
  fridgeModel?: string
}

export function FilterReminderForm({ filterModel, fridgeModel }: FilterReminderFormProps) {
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [honeypot, setHoneypot] = useState("")
  const [optIn, setOptIn] = useState(false)
  const [model, setModel] = useState(filterModel ?? "")
  const { status, errorMessage, subscribe } = useAlertSubscribe()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Cadence is fixed at 6 months in the UI (the standard replacement
    // interval); the backend accepts 1-24 and defaults to 6 when omitted.
    void subscribe({
      type: "filter_reminder",
      email,
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      honeypot,
      generalUpdatesOptIn: optIn,
      filterModel: model,
      fridgeModel: fridgeModel || undefined,
    })
  }

  if (status === "success") {
    return (
      <div
        className="bg-white rounded-xl p-5 border border-gray-200"
        data-testid="filter-reminder-form"
        data-state="success"
      >
        <p className="text-sm font-semibold text-gray-900">Almost there — check your inbox.</p>
        <p className="text-xs text-gray-600 mt-1">
          We just emailed you a confirmation link. Your reminder starts only after you click it —
          nothing arrives until you confirm.
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-xl p-5 border border-gray-200"
      data-testid="filter-reminder-form"
    >
      <p className="text-sm font-bold text-gray-900">Never forget a filter change</p>
      <p className="text-xs text-gray-600 mt-1">
        We&rsquo;ll email you every 6 months — the standard replacement interval — when your filter
        is due.
      </p>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name (optional)"
          autoComplete="given-name"
          maxLength={60}
          className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="First name (optional)"
        />
        <input
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name (optional)"
          autoComplete="family-name"
          maxLength={60}
          className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="Last name (optional)"
        />
      </div>
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          required
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="Filter model (e.g. LT1000P)"
          className="sm:w-48 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="Water filter model"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="Email address for filter reminders"
        />
        <button
          type="submit"
          disabled={status === "submitting" || !email.trim() || !model.trim()}
          className="px-4 py-2 text-xs font-bold uppercase tracking-wide bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] rounded-md transition-colors disabled:opacity-100 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? "Saving…" : "Remind me"}
        </button>
      </div>
      {/* Honeypot — hidden from real users; bots that fill it are rejected. */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <label className="mt-2 flex items-start gap-2 text-xs text-gray-600">
        <input
          type="checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
          className="mt-0.5"
          data-testid="general-updates-optin"
        />
        Also send me occasional Fridge.com updates
      </label>
      {status === "error" && errorMessage && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {errorMessage}
        </p>
      )}
      <p className="mt-2 text-[11px] text-gray-500">
        We may earn an affiliate commission when you buy through our links. This never affects
        our reviews or comparisons.{" "}
        <Link href="/privacy-policy" className="underline hover:text-gray-700">
          Privacy policy
        </Link>
      </p>
    </form>
  )
}

export default FilterReminderForm
